import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi } from '../../api/finance';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  approved: 'Approved', rejected: 'Rejected', posted: 'Posted', reversed: 'Reversed',
};

interface FormValues {
  entryDate: string; description: string; fiscalYearId: string; accountingPeriodId: string; currencyCode: string;
  lines: { accountId: string; description: string; debitAmount: string; creditAmount: string; projectId: string }[];
}

export function JournalEntriesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const journalsQuery = useQuery({ queryKey: ['finance-journals'], queryFn: () => financeApi.listJournalEntries() });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts(), enabled: showForm });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: showForm });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: showForm });
  const [selectedFiscalYearId, setSelectedFiscalYearId] = useState<number | null>(null);
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', selectedFiscalYearId], queryFn: () => financeApi.getFiscalYear(selectedFiscalYearId!), enabled: !!selectedFiscalYearId });

  const { register, control, handleSubmit, reset } = useForm<FormValues>({ defaultValues: { currencyCode: 'NGN', lines: [{ accountId: '', description: '', debitAmount: '', creditAmount: '', projectId: '' }, { accountId: '', description: '', debitAmount: '', creditAmount: '', projectId: '' }] } });
  const linesArray = useFieldArray({ control, name: 'lines' });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-journals'] });

  const createMutation = useMutation({
    mutationFn: (v: FormValues) => financeApi.createJournalEntry({
      entryDate: v.entryDate, description: v.description, fiscalYearId: Number(v.fiscalYearId), accountingPeriodId: Number(v.accountingPeriodId),
      currencyCode: v.currencyCode,
      lines: v.lines.map((l) => ({
        accountId: Number(l.accountId), description: l.description || undefined,
        debitAmount: l.debitAmount ? Number(l.debitAmount) : undefined, creditAmount: l.creditAmount ? Number(l.creditAmount) : undefined,
        projectId: l.projectId ? Number(l.projectId) : undefined,
      })),
    }),
    onSuccess: (result) => { showSuccess(`Journal entry created - ${result.data.journalReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create journal entry.'),
  });

  const submitMutation = useMutation({ mutationFn: (id: number) => financeApi.submitJournalEntry(id), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const reviewMutation = useMutation({ mutationFn: (id: number) => financeApi.reviewJournalEntry(id), onSuccess: () => { showSuccess('Moved to review.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decideMutation = useMutation({
    mutationFn: ({ id, decision, comment }: { id: number; decision: 'approved' | 'rejected'; comment: string }) => financeApi.decideJournalEntry(id, decision, comment),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const returnMutation = useMutation({ mutationFn: ({ id, comment }: { id: number; comment: string }) => financeApi.returnJournalEntry(id, comment), onSuccess: () => { showSuccess('Returned for redraft.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const resubmitMutation = useMutation({ mutationFn: (id: number) => financeApi.resubmitJournalEntry(id), onSuccess: () => { showSuccess('Resubmitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const postMutation = useMutation({
    mutationFn: (id: number) => financeApi.postJournalEntry(id),
    onSuccess: (result) => { showSuccess(`Posted - debits and credits both ${result.data.totalDebit.toLocaleString()}.`); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to post - the journal may not be balanced.'),
  });
  const reverseMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => financeApi.reverseJournalEntry(id, reason),
    onSuccess: (result) => { showSuccess(`Reversed - new journal ${result.data.reversalReference}.`); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const periods = selectedFiscalYearId ? fiscalYearDetailQuery.data?.periods ?? [] : [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Journal Entries</h1>
        <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Journal Entry'}</button>
      </div>

      {showForm && (
        <section className="card">
          <h2>New Journal Entry</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Entry Date<input type="date" {...register('entryDate', { required: true })} /></label>
            <label>Description<input {...register('description', { required: true })} /></label>
            <label>
              Fiscal Year
              <select {...register('fiscalYearId', { required: true, onChange: (e) => setSelectedFiscalYearId(Number(e.target.value) || null) })}>
                <option value="">- select -</option>
                {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
              </select>
            </label>
            <label>
              Accounting Period
              <select {...register('accountingPeriodId', { required: true })}>
                <option value="">- select -</option>
                {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
              </select>
            </label>
            <label>Currency<input {...register('currencyCode')} placeholder="NGN" /></label>

            <h3>Lines</h3>
            {linesArray.fields.map((field, i) => (
              <div key={field.id} className="p11-repeat-block">
                <label>
                  Account
                  <select {...register(`lines.${i}.accountId`, { required: true })}>
                    <option value="">- select -</option>
                    {accountsQuery.data?.data.filter((a) => a.posting_allowed).map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                  </select>
                </label>
                <label>Description<input {...register(`lines.${i}.description`)} /></label>
                <label>Debit<input type="number" step="0.01" {...register(`lines.${i}.debitAmount`)} /></label>
                <label>Credit<input type="number" step="0.01" {...register(`lines.${i}.creditAmount`)} /></label>
                <label>
                  Project (optional)
                  <select {...register(`lines.${i}.projectId`)}>
                    <option value="">- none -</option>
                    {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </label>
                {linesArray.fields.length > 2 && <button type="button" className="link-button" onClick={() => linesArray.remove(i)}>Remove Line</button>}
              </div>
            ))}
            <button type="button" onClick={() => linesArray.append({ accountId: '', description: '', debitAmount: '', creditAmount: '', projectId: '' })}>+ Add Line</button>

            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <section className="card">
        {journalsQuery.data && (
          <DataTable
            rows={journalsQuery.data.data}
            rowKey={(j) => j.id}
            emptyMessage="No journal entries yet."
            columns={[
              { header: 'Reference', render: (j) => j.journal_reference },
              { header: 'Date', render: (j) => j.entry_date },
              { header: 'Description', render: (j) => j.description },
              { header: 'Period', render: (j) => j.period_label ?? '-' },
              { header: 'Status', render: (j) => <StatusBadge status={j.status} label={STATUS_LABELS[j.status]} /> },
              {
                header: '',
                render: (j) => (
                  <span className="row-actions">
                    {j.status === 'draft' && <button className="link-button" onClick={() => submitMutation.mutate(j.id)}>Submit</button>}
                    {j.status === 'submitted' && <button className="link-button" onClick={() => reviewMutation.mutate(j.id)}>Move to Review</button>}
                    {(j.status === 'submitted' || j.status === 'under_review') && (
                      <button className="link-button" onClick={() => { const c = window.prompt('Return comment:'); if (c) returnMutation.mutate({ id: j.id, comment: c }); }}>Return</button>
                    )}
                    {j.status === 'returned' && <button className="link-button" onClick={() => resubmitMutation.mutate(j.id)}>Resubmit</button>}
                    {j.status === 'under_review' && (
                      <>
                        <button className="link-button" onClick={() => { const c = window.prompt('Approval comment:'); if (c) decideMutation.mutate({ id: j.id, decision: 'approved', comment: c }); }}>Approve</button>
                        <button className="link-button" onClick={() => { const c = window.prompt('Rejection reason:'); if (c) decideMutation.mutate({ id: j.id, decision: 'rejected', comment: c }); }}>Reject</button>
                      </>
                    )}
                    {j.status === 'approved' && <button className="link-button" onClick={() => postMutation.mutate(j.id)} disabled={postMutation.isPending}>Post</button>}
                    {j.status === 'posted' && <button className="link-button" onClick={() => { const r = window.prompt('Reason for reversal:'); if (r) reverseMutation.mutate({ id: j.id, reason: r }); }}>Reverse</button>}
                    <Link to={`/finance/journal-entries/${j.id}`}>View</Link>
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
}
